import { useState, useEffect } from "react";
import { H1Title, Paragraph } from "../../components/typography/typography";
import { AppButton } from "../../components/buttons/button";
import SubmissionService from "../../services/submissionService";
import CompetitionService from "../../services/competitionService";
import { Success, Error as ToastError } from "../../components/toast/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faClock, faExternalLinkAlt, faUndo, faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";

export default function AdminFundraising() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [competitionId, setCompetitionId] = useState(null);
  const [roundFilter, setRoundFilter] = useState('round5');
  const [expanded, setExpanded] = useState(new Set());
  const [busySource, setBusySource] = useState(null); // `${submissionId}:${sourceId}`

  useEffect(() => { loadCompetition(); }, []);
  useEffect(() => { if (competitionId) loadSubmissions(); }, [competitionId, roundFilter]);

  const loadCompetition = async () => {
    try {
      const { data } = await CompetitionService.getActive();
      setCompetitionId(data.data.competition._id);
    } catch {
      ToastError('No active competition found');
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const { data } = await SubmissionService.getAll({
        competitionId,
        currentRound: roundFilter,
        limit: 200
      });
      setSubmissions(data.data.submissions || []);
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed to load');
    } finally { setLoading(false); }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleToggleVerify = async (submissionId, sourceId, verified) => {
    setBusySource(`${submissionId}:${sourceId}`);
    try {
      if (verified) {
        await SubmissionService.unverifyFundraisingSource(submissionId, sourceId);
        Success('Source unverified');
      } else {
        await SubmissionService.verifyFundraisingSource(submissionId, sourceId);
        Success('Source verified');
      }
      await loadSubmissions();
    } catch (err) {
      ToastError(err.response?.data?.error?.message || 'Failed');
    } finally { setBusySource(null); }
  };

  if (loading) return <div className="py-20 text-center text-grey-667">Loading...</div>;

  const totalSources = submissions.reduce((n, s) => n + (s.fundraising?.sources?.length || 0), 0);
  const pendingSources = submissions.reduce((n, s) => n + (s.fundraising?.sources?.filter(x => !x.verifiedAt).length || 0), 0);

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <H1Title size="sm">Fundraising Verification</H1Title>
          <Paragraph size="sm" className="text-grey-667">
            {submissions.length} team{submissions.length === 1 ? '' : 's'} · {pendingSources} pending / {totalSources} total sources
          </Paragraph>
        </div>
        <select
          value={roundFilter}
          onChange={(e) => setRoundFilter(e.target.value)}
          className="border border-grey-200 rounded-md px-3 py-2 text-sm outline-none"
        >
          <option value="round5">Round 5 — Fundraising & Logistics</option>
          <option value="round6">Round 6 — Implementation</option>
          <option value="implementation">Implementation Phase</option>
        </select>
      </div>

      {submissions.length === 0 ? (
        <div className="bg-white rounded-xl border border-grey-200 p-12 text-center text-grey-667">
          No submissions in this phase yet.
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map((sub) => {
            const isOpen = expanded.has(sub._id);
            const target = sub.fundraising?.targetAmount || 0;
            const sources = sub.fundraising?.sources || [];
            const verifiedTotal = sources.filter(s => s.verifiedAt).reduce((n, s) => n + (s.amount || 0), 0);
            const reportedTotal = sources.reduce((n, s) => n + (s.amount || 0), 0);
            const pct = target > 0 ? Math.min((verifiedTotal / target) * 100, 100) : 0;
            const fullyFunded = target > 0 && verifiedTotal >= target;

            return (
              <div key={sub._id} className="bg-white rounded-xl border border-grey-200 overflow-hidden">
                <button
                  onClick={() => toggleExpand(sub._id)}
                  className="w-full flex items-center gap-4 px-5 py-4 hover:bg-grey-bg/50 cursor-pointer text-left"
                >
                  <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} className="text-grey-98 text-xs flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{sub.submitterId?.firstName} {sub.submitterId?.lastName}</p>
                    <p className="text-xs text-grey-98 truncate">{sub.teamId?.name || 'Individual'} · {sub.submitterId?.email}</p>
                  </div>
                  <div className="w-32 flex-shrink-0">
                    <div className="w-full bg-grey-200 rounded-full h-2 mb-1">
                      <div className={`h-2 rounded-full ${fullyFunded ? 'bg-green' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-xs text-grey-667 font-mono">${verifiedTotal.toLocaleString()} / ${target.toLocaleString()}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full font-semibold flex-shrink-0 ${fullyFunded ? 'bg-green-light text-green' : 'bg-yellow-500/10 text-orange-dark'}`}>
                    {fullyFunded ? 'Funded' : `${Math.round(pct)}%`}
                  </span>
                </button>

                {isOpen && (
                  <div className="border-t border-grey-200 px-5 py-4 bg-grey-bg/30">
                    <div className="flex items-center justify-between mb-3 text-xs text-grey-667">
                      <span>Target: <b>${target.toLocaleString()}</b></span>
                      <span>Verified: <b className="text-green">${verifiedTotal.toLocaleString()}</b></span>
                      <span>Reported: <b>${reportedTotal.toLocaleString()}</b></span>
                    </div>
                    {sources.length === 0 ? (
                      <p className="text-sm text-grey-98 text-center py-6">No sources submitted yet.</p>
                    ) : (
                      <div className="divide-y divide-grey-200 bg-white rounded-lg border border-grey-200">
                        {sources.map((s) => {
                          const key = `${sub._id}:${s._id}`;
                          const busy = busySource === key;
                          return (
                            <div key={s._id} className="flex items-center justify-between px-4 py-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold">{s.name}</p>
                                <div className="flex items-center gap-3 mt-1">
                                  {s.proofUrl ? (
                                    <a href={s.proofUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                                      <FontAwesomeIcon icon={faExternalLinkAlt} /> Proof
                                    </a>
                                  ) : (
                                    <span className="text-xs text-grey-98 italic">No proof uploaded</span>
                                  )}
                                  {s.verifiedAt && (
                                    <span className="text-xs text-grey-98">Verified {new Date(s.verifiedAt).toLocaleDateString()}</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                                <p className="font-bold text-sm">${s.amount.toLocaleString()}</p>
                                {s.verifiedAt ? (
                                  <AppButton onClick={() => handleToggleVerify(sub._id, s._id, true)} loading={busy} variant="outline" size="sm" fullWidth={false} className="px-3">
                                    <FontAwesomeIcon icon={faUndo} className="mr-1" /> Unverify
                                  </AppButton>
                                ) : (
                                  <AppButton onClick={() => handleToggleVerify(sub._id, s._id, false)} loading={busy} size="sm" fullWidth={false} className="px-3">
                                    <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Verify
                                  </AppButton>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
